export interface TimeZoneOption {
    value: string;
    label: string;
}

export const COMMON_TIME_ZONES = [
    "Asia/Taipei",
    "Asia/Tokyo",
    "Asia/Hong_Kong",
    "Asia/Shanghai",
    "Asia/Singapore",
    "UTC",
    "America/New_York",
    "America/Los_Angeles",
    "Europe/London",
] as const;

export function getUserTimeZone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Taipei";
    } catch {
        return "Asia/Taipei";
    }
}

export function formatTimeZoneLabel(tz: string): string {
    try {
        const offset = new Intl.DateTimeFormat("en-US", {
            timeZone: tz,
            timeZoneName: "shortOffset",
        })
            .formatToParts(new Date())
            .find((p) => p.type === "timeZoneName")?.value;

        return offset ? `${tz} (${offset})` : tz;
    } catch {
        return tz;
    }
}

let cachedBaseOptions: TimeZoneOption[] | null = null;

function getBaseTimeZoneOptions(): TimeZoneOption[] {
    if (cachedBaseOptions) {
        return cachedBaseOptions;
    }

    let supported: string[] = [];
    try {
        supported = typeof Intl.supportedValuesOf === "function"
            ? Intl.supportedValuesOf("timeZone")
            : [];
    } catch {
        supported = [];
    }

    const commonSet = new Set<string>(COMMON_TIME_ZONES);
    const commonOptions: TimeZoneOption[] = COMMON_TIME_ZONES.map((tz) => ({
        value: tz,
        label: formatTimeZoneLabel(tz),
    }));

    const otherOptions: TimeZoneOption[] = supported
        .filter((tz) => !commonSet.has(tz))
        .map((tz) => ({
            value: tz,
            label: formatTimeZoneLabel(tz),
        }));

    cachedBaseOptions = [...commonOptions, ...otherOptions];
    return cachedBaseOptions;
}

export function getTimeZoneOptions(currentTimeZone?: string): TimeZoneOption[] {
    const base = getBaseTimeZoneOptions();
    if (currentTimeZone && !base.some((o) => o.value === currentTimeZone)) {
        return [{ value: currentTimeZone, label: formatTimeZoneLabel(currentTimeZone) }, ...base];
    }
    return base;
}
